import { React, bind } from 'Imports';
import { Grid } from 'MaterialUIComponents';
import { SharedTabs, SharedTab } from '$Components/Shared/SharedTabs/SharedTabs';
import { EventData } from '$Components/VideoEvents/VideoEventDetails/EventData';
import { DetailsSidePanelSplitOff } from '$Components/VideoEvents/VideoEventDetails/DetailsSidePanel_SplitOff';
import { VideoEventHistoryDetails } from '$Components/VideoEvents/VideoEventDetails/VideoEventHistoryDetails';
import { EventComments } from '$Components/VideoEvents/VideoEventDetails/EventComments/EventComments';
import { PageHeader } from '$Components/Shared/PageHeader';
import { TermsOfUse } from '$Components/Shared/TermsOfUse';
import { VideoEventWorkflowStatusIdEnum, VideoEventResponse, VideoEventHistoryTableResponse } from '$Generated/api';
import { IPricingPlanServiceInjectedProps, PricingPlanService } from '$State/PricingPlanFreezerService';
import { IVideoEventServiceInjectedProps, VideoEventService } from '$State/VideoEventFreezerService';
import { IConfigServiceInjectedProps, ConfigService } from '$State/ConfigFreezerService';
import { IIntegrationPartnerDataInjectedProps, IntegrationPartnerDataService } from '$State/IntegrationPartnerDataFreezerService';
import { SpeedGraph as SpeedGraphSplitOff } from '$Components/Charts/SpeedGraph_SplitOff';
import SpeedGraph from '$Components/Charts/SpeedGraph';
import { canEditByRole, RolesEnum } from '$Externals/VerifyRole';
import { Error } from '$Pages/Error/Error';
import VideoViews from '$Components/VideoViews/VideoViews';
import { getSplitUserAttributes, TypedSplitTreatments, splitTreatmentNames, isSplitTreatmentOn } from '$Utilities/split-io-sdk';
import { ISplitTreatmentsChildProps } from '@splitsoftware/splitio-react/types/types';
import { isEncompass } from '$Utilities/integrationPlatform';
import { CourtReporter } from '$Components/VideoEvents/VideoEventDetails/CourtReporter/CourtReporter';
import VideoEventDetailsSidePanel from '$Components/VideoEventDetails/SidePanel/VideoEventDetailsSidePanel';
import AiOverlayProvider from '$State/AiOverlay/AiOverlayProvider';
import { EventLocationMap } from '$Components/VideoEvents/VideoEventDetails/EventLocationMap';
import classNames from 'classnames';

const styles = require('$Pages/VideoEventDetails/VideoEventDetails.scss') as {
    main: string;
    mainEncompass: string;
    content: string;
    contentSplitOff: string;
    detailsContentAndSidePanel: string;
    withSidePanel: string;
    detailsContent: string;
    detailsSectionContainerEncompass: string;
    detailsSectionContainerEncompassLocationMap: string;
    detailsSectionContainerGeotab: string;
    tabsContainer: string;
};

interface IVideoEventDetailsBaseProps {
    eventId: string;
    onSelectVideoEvent: (value: string, goBack: boolean, addHistoryState?: boolean) => void;
    isVideoRecall: boolean;
    subText: string;
}

interface IVideoEventDetailsState {
    data?: VideoEventResponse;
    hasCourtReporter: boolean;
    eventStatus: VideoEventWorkflowStatusIdEnum;
    errors: boolean;
    showDeletePopup: boolean;
    eventDetailsTabValue: string;
    eventHistoryTable?: VideoEventHistoryTableResponse[];
    historyRefreshKey: number;
}

type IVideoEventDetailsProps = IVideoEventDetailsBaseProps &
    IPricingPlanServiceInjectedProps &
    IVideoEventServiceInjectedProps &
    IConfigServiceInjectedProps &
    IIntegrationPartnerDataInjectedProps;

class _VideoEventDetailsPage extends React.Component<IVideoEventDetailsProps, IVideoEventDetailsState> {
    constructor(props: IVideoEventDetailsProps) {
        super(props);
    }

    state = {
        data: undefined,
        hasCourtReporter: false,
        eventStatus: 'New',
        errors: false,
        showDeletePopup: false,
        eventDetailsTabValue: 'Videos',
        eventHistoryTable: undefined,
        historyRefreshKey: 0,
    } as IVideoEventDetailsState;

    async loadVideoEventInfo() {
        try {
            await this.props.videoEvents.getVideoEvent(this.props.eventId);
            const freezer = this.props.videoEvents.getState();
            const { selectedVideoEventResult, videoEventHistoryTableResults } = freezer;
            const data = selectedVideoEventResult.data || undefined;

            this.setState({
                data: data,
                hasCourtReporter: await this.props.pricingPlan.hasFeatureType('CourtReporter'),
                eventStatus: data?.videoEventStatus || 'New',
                eventHistoryTable: videoEventHistoryTableResults.data?.data,
                errors: false,
            });
        } catch (error) {
            this.setState({
                data: undefined,
                errors: true,
            });
        }
    }

    async componentDidUpdate(prevProps: Readonly<IVideoEventDetailsProps>): Promise<void> {
        if (this.props.eventId != prevProps.eventId) {
            this.setState({ data: undefined });
            await this.loadVideoEventInfo();
            this.setState({ eventDetailsTabValue: 'Videos' });
        }
    }

    async componentDidMount(): Promise<void> {
        window.addEventListener('popstate', this.handlePopstate, false);
        this.loadVideoEventInfo();
    }

    async componentWillUnmount(): Promise<void> {
        window.removeEventListener('popstate', this.handlePopstate, false);
    }

    routerWillLeave(): void {
        this.props.onSelectVideoEvent('', false);
    }

    @bind
    setStatus(status: string | undefined) {
        const freezer = this.props.videoEvents.getState();
        const { selectedVideoEventResult } = freezer;
        const data = selectedVideoEventResult.data ? selectedVideoEventResult.data : {};
        if (data.videoEventStatus !== status) {
            this.props.videoEvents.updateLocalVideoEventStatus(status as VideoEventWorkflowStatusIdEnum, Number(data.id));
            this.setState({ eventStatus: status as VideoEventWorkflowStatusIdEnum });
        }
    }

    // centralized update of video event history to reduce feature enabled checks
    @bind
    async updateVideoEventHistory() {
        const id = this.state.data?.id;

        if (id && this.state.hasCourtReporter) {
            await this.props.videoEvents.getVideoEventHistoryTable(id);
            this.setState((prev) => ({ historyRefreshKey: prev.historyRefreshKey + 1 }));
        }
    }

    @bind
    async updateVideoState(isStarting: boolean, isEnding: boolean): Promise<any> {
        const freezer = this.props.videoEvents;
        const id = this.state.data?.id || 0;
        const { selectedVideoEventResult } = freezer.getState();
        const eventName = this.props.isVideoRecall ? 'recall' : 'event';

        if (isStarting) {
            // only update status from New
            if (
                selectedVideoEventResult.data?.videoEventStatus === 'New' &&
                canEditByRole([RolesEnum.videoEventEdit, RolesEnum.encompassVideo])
            ) {
                this.setStatus('In_Progress'); // ensure subsequent plays do not send duplicate status updates
                await freezer.updateVideoEventWorkflowStatus(id, 'In_Progress');
            }

            await freezer.addVideoEventActionRecord(id, 'PlayedVideo', `Videos started from video ${eventName} details page`);
            await this.updateVideoEventHistory();
        } else if (isEnding) {
            await freezer.addVideoEventActionRecord(id, 'VideosWatched', `Videos watched from video ${eventName} details page`);
            await this.updateVideoEventHistory();
        }
    }

    @bind
    handleEventDetailsTabChange(_event: React.ChangeEvent<{}>, newValue: string) {
        this.setState({ eventDetailsTabValue: newValue });
    }

    @bind
    selectLocationMapTab(): void {
        this.setState({ eventDetailsTabValue: 'Map' });
    }

    @bind
    createVideoLayout(
        data: VideoEventResponse | undefined,
        isVideoRecall: boolean,
        isEventDetailsRedesignSplitOn: boolean,
        showLocationMapTab: boolean,
    ): JSX.Element {
        const map = data ? (
            <EventData
                mapUrl={this.props.integrationPartnerData.getIntegrationPartnerManager().getMapUrl(data)}
                event={data}
                latitude={data?.latitude || 0}
                longitude={data?.longitude || 0}
            />
        ) : undefined;

        const history = data ? (
            <VideoEventHistoryDetails
                event={data}
                updateVideoEventHistory={this.updateVideoEventHistory}
                isEventDetailsRedesignSplitOn={isEventDetailsRedesignSplitOn}
            />
        ) : undefined;

        const { eventDetailsTabValue } = this.state;
        const videoHasSpeedGraph = data?.speedGraphPoints && data.speedGraphPoints.length > 0 && !isEncompass;

        const videosContent = (
            <VideoViews
                videoAssetsRaw={data?.videoAsset ?? []}
                videoEventId={data!.id!}
                videoEventDisplayId={data!.displayId!}
                videoStatus={data!.videoEventStatus!}
                isVideoRecall={isVideoRecall}
                onVideosStarted={() => this.updateVideoState(true, false)}
                onVideosWatched={() => this.updateVideoState(false, true)}
                onVideoDownloaded={this.updateVideoEventHistory}
            />
        );

        return (
            <>
                {isEventDetailsRedesignSplitOn ? (
                    <>
                        <SharedTabs
                            value={eventDetailsTabValue}
                            onChange={this.handleEventDetailsTabChange}
                            flexContainerClassName={styles.tabsContainer}
                        >
                            <SharedTab label="Videos" value="Videos" />

                            {showLocationMapTab && <SharedTab label="Location Map" value="Map" />}
                            <SharedTab label="Comments" value="Comments" />
                            <SharedTab label="Court Reporter" value="CourtReporter" />
                        </SharedTabs>
                        <div
                            style={{ display: eventDetailsTabValue === 'Videos' ? undefined : 'none' }}
                            className={isEncompass ? styles.detailsSectionContainerEncompass : styles.detailsSectionContainerGeotab}
                        >
                            {videosContent}

                            {videoHasSpeedGraph && data.videoEventTypePairings !== undefined && data.videoEventTypePairings.length > 0 && (
                                <div style={{ padding: '16px 0' }}>
                                    <div>
                                        <SpeedGraph
                                            data={data.speedGraphPoints!}
                                            eventStartDate={data.eventStartDate!}
                                            videoEventTypePairings={data.videoEventTypePairings}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        {showLocationMapTab && eventDetailsTabValue === 'Map' && (
                            <div className={styles.detailsSectionContainerEncompassLocationMap}>
                                <EventLocationMap latitude={data?.latitude ?? 0} longitude={data?.longitude ?? 0} />
                            </div>
                        )}
                        <div
                            style={{ display: eventDetailsTabValue === 'Comments' ? undefined : 'none' }}
                            className={isEncompass ? styles.detailsSectionContainerEncompass : styles.detailsSectionContainerGeotab}
                        >
                            <EventComments videoEventId={data?.id || 0} />
                        </div>
                        {eventDetailsTabValue === 'CourtReporter' && (
                            <div className={isEncompass ? styles.detailsSectionContainerEncompass : styles.detailsSectionContainerGeotab}>
                                <CourtReporter event={data ?? ({} as VideoEventResponse)} refreshKey={this.state.historyRefreshKey} />
                            </div>
                        )}
                    </>
                ) : (
                    <div style={{ padding: 10 }}>
                        {videosContent}
                        <div style={{ padding: 10 }}>
                            {videoHasSpeedGraph && data.videoEventTypePairings !== undefined && data.videoEventTypePairings.length > 0 && (
                                <div>
                                    <SpeedGraphSplitOff
                                        width={800}
                                        height={122}
                                        speedGraphData={data.speedGraphPoints!}
                                        eventStartDate={data.eventStartDate}
                                        eventTypeData={data.videoEventTypePairings}
                                        liveTime={0}
                                    />
                                </div>
                            )}
                            <Grid container spacing={1} justifyContent={'space-evenly'}>
                                <Grid item sm={12} md={6}>
                                    {map}
                                </Grid>
                                <Grid item sm={12} md={6}>
                                    {history}
                                </Grid>
                            </Grid>
                        </div>
                    </div>
                )}
            </>
        );
    }

    @bind
    handlePopstate(event: PopStateEvent): void {
        const { onSelectVideoEvent, eventId } = this.props;
        if (event && eventId !== '') {
            VideoEventService.clearVideoEvents();
            onSelectVideoEvent('', false);
        }
    }

    @bind
    toggleDeletePopup(): void {
        this.setState({ showDeletePopup: !this.state.showDeletePopup });
    }

    render(): JSX.Element {
        const { data, eventStatus, errors } = this.state;
        const { isVideoRecall, subText } = this.props;
        const pageTitle = isVideoRecall ? 'Video Recall Details' : 'Video Event Details';
        const navLinkDisplay = isVideoRecall ? '< Back to All Video Recalls' : '< Back to All Videos';
        const pageSubTitle = isVideoRecall ? undefined : subText;

        return (
            <>
                <TermsOfUse />
                {errors ? (
                    <Error messages={['<b>Error:</b> Event ID not found.']} showGoBack={true} />
                ) : (
                    <div className={classNames(styles.main, isEncompass ? styles.mainEncompass : '')}>
                        <PageHeader
                            pageTitle={pageTitle}
                            pageSubTitle={pageSubTitle}
                            navLinkDisplay={navLinkDisplay}
                            onSelectVideoEvent={() => this.props.onSelectVideoEvent('', true)}
                            fixedHeight={false}
                        />
                        {!data ? (
                            <div>Loading video event...</div>
                        ) : (
                            <TypedSplitTreatments
                                names={[splitTreatmentNames.eventDetailsRedesign, splitTreatmentNames.eventDetailsLocationMap]}
                                attributes={getSplitUserAttributes()}
                            >
                                {({ treatments, isReady, isTimedout }: ISplitTreatmentsChildProps) => {
                                    if (!isReady && !isTimedout) {
                                        return <div>Loading video event...</div>;
                                    }

                                    const isEventDetailsRedesignSplitOn =
                                        (isReady || isTimedout) && isSplitTreatmentOn(treatments[splitTreatmentNames.eventDetailsRedesign]);
                                    const isLocationMapSplitOn =
                                        (isReady || isTimedout) && isSplitTreatmentOn(treatments[splitTreatmentNames.eventDetailsLocationMap]);
                                    const hasEventLocation =
                                        !!data?.latitude && data.latitude !== 0 && !!data?.longitude && data.longitude !== 0;
                                    const showLocationMapTab = isEncompass && isLocationMapSplitOn && hasEventLocation;
                                    const locationLinkUrl =
                                        !isEncompass &&
                                        isLocationMapSplitOn &&
                                        hasEventLocation &&
                                        data.telematicDeviceSourceId &&
                                        data.eventStartDate &&
                                        data.eventEndDate
                                            ? this.props.integrationPartnerData.getIntegrationPartnerManager().getMapUrl(data)
                                            : undefined;

                                    return (
                                        <div className={isEventDetailsRedesignSplitOn ? styles.content : styles.contentSplitOff}>
                                            <AiOverlayProvider
                                                key={this.props.eventId}
                                                eventId={this.props.eventId}
                                                videoEvent={data}
                                                onVideoEventRefreshed={() => this.loadVideoEventInfo()}
                                            >
                                                <div
                                                    className={classNames(styles.detailsContentAndSidePanel, {
                                                        [styles.withSidePanel]: isEventDetailsRedesignSplitOn,
                                                    })}
                                                >
                                                    {isEventDetailsRedesignSplitOn && (
                                                        <VideoEventDetailsSidePanel
                                                            videoEvent={data}
                                                            isVideoRecall={isVideoRecall}
                                                            onSelectVideoEvent={this.props.onSelectVideoEvent}
                                                            onVideoEventUpdated={this.updateVideoEventHistory}
                                                            showLocationMapTab={showLocationMapTab}
                                                            onLocationMapClick={this.selectLocationMapTab}
                                                            locationLinkUrl={locationLinkUrl}
                                                        />
                                                    )}
                                                    <div className={styles.detailsContent}>
                                                        {this.createVideoLayout(
                                                            data,
                                                            isVideoRecall,
                                                            isEventDetailsRedesignSplitOn,
                                                            showLocationMapTab,
                                                        )}
                                                    </div>
                                                    {!isEventDetailsRedesignSplitOn && (
                                                        <DetailsSidePanelSplitOff
                                                            eventStatus={eventStatus}
                                                            event={data}
                                                            onStatusChange={this.setStatus}
                                                            updateVideoEventHistory={this.updateVideoEventHistory}
                                                            onSelectVideoEvent={this.props.onSelectVideoEvent}
                                                            isVideoRecall={isVideoRecall}
                                                            toggleDeletePopup={this.toggleDeletePopup}
                                                        />
                                                    )}
                                                </div>
                                            </AiOverlayProvider>
                                        </div>
                                    );
                                }}
                            </TypedSplitTreatments>
                        )}
                    </div>
                )}
            </>
        );
    }
}

export const VideoEventDetailsPage = PricingPlanService.inject(
    VideoEventService.inject(ConfigService.inject(IntegrationPartnerDataService.inject(_VideoEventDetailsPage))),
);
